import { GridColumn } from '../../models/work-package-view.model';
import { ColumnType, DataType } from './views.enums';

/**
 * How a list page's reporting view offers filters and asks for columns. Each list page shares its
 * own with its dashboard widget, so both offer the same "Add filter" menu and run the same columns.
 */
export interface ListViewFilters {
  /** Raw columns the row mapping needs but the column options don't offer. */
  extraRunColumns: string[];
  /** Column options never offered in the filter builder (lower-case keys). */
  excludedFilterFields: Set<string>;
  /** Shapes one offered column option for the filter builder. */
  filterColumn(col: GridColumn): GridColumn;
  /** Filter-only columns picked from a list rather than typed. */
  pickerFilterColumns: GridColumn[];
}

/** The filter builder's columns: the meaningful column options, shaped, then the pickers. */
export function filterBuilderColumns(filters: ListViewFilters, columns: GridColumn[]): GridColumn[] {
  return [
    ...columns
      .filter(col => !filters.excludedFilterFields.has(col.field?.toLowerCase() ?? ''))
      .map(col => filters.filterColumn(col)),
    ...filters.pickerFilterColumns,
  ];
}

/** Column options kept out of the "Add filter" menu — they stay pickable as view columns. */
export function withoutFilter(fields: string[]): (col: GridColumn) => GridColumn {
  const set = new Set(fields);
  return (col) => (set.has(col.field) ? { ...col, allowFiltering: false } : col);
}

/** A filter-only column matched on a user id (a user field, so the filter row offers its user dropdown). */
export const userPickerColumn = (field: string, headerText: string): GridColumn =>
  ({ field, headerText, dataType: DataType.NUMBER, columnType: ColumnType.STANDARD, isSelectable: false });

/** A filter-only Template picker; its options load in the background (getTemplateFilterOptions). */
export const templatePickerColumn = (field: string, headerText: string): GridColumn =>
  ({ field, headerText, dataType: DataType.DROPDOWN, columnType: ColumnType.STANDARD, isSelectable: false, lookupOptions: [] });

/** Gives each custom column with known dropdown options (CustomFieldsService.getDropdownOptions) its `options`. */
export function withCustomFieldOptions(columns: GridColumn[], options: Map<string, string[]>): GridColumn[] {
  if (options.size === 0) return columns;
  return columns.map(col =>
    col.columnType === ColumnType.CUSTOM && options.has(col.field) ? { ...col, options: options.get(col.field) } : col
  );
}
